import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Plus, Edit, MapPin, Building2, RefreshCw, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { timeEntries as timeApi } from '@/utils/api'
import { useTimeEntries } from '@/hooks/useTimeEntries'
import { useUsers } from '@/hooks/useUsers'
import { useProjects } from '@/hooks/useProjects'
import { formatTime, formatHours } from '@/utils/formatters'
import { cn } from '@/lib/utils'

function getWeekStart(date: Date) {
  const d = new Date(date)
  const day = d.getDay()
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1))
  return d
}

interface TimeEntry { id: number; user_id: number; user_name: string; type: string; time?: string; timestamp: string; date?: string; project_name?: string; project_id?: number; auto_assigned?: boolean; location_url?: string }
interface UserOpt { user_id?: number; id: number; name: string }
interface ProjectOpt { id: number; name: string }
interface GroupedUser { user_name: string; user_id: number; entries: TimeEntry[]; totalWorkMinutes: number; workHours: number | null }

function AddEntryForm({ users, projects, onClose, onSuccess }: { users: UserOpt[]; projects: ProjectOpt[]; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({ user_id: '', type: 'Kommen', timestamp: new Date().toISOString().slice(0, 16), project_id: '', reason: '' })
  const [saving, setSaving] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.user_id || !formData.reason) { toast.error('Bitte alle Pflichtfelder ausfuellen'); return }
    setSaving(true)
    try { await timeApi.create({ ...formData, user_id: parseInt(formData.user_id), timestamp: new Date(formData.timestamp).toISOString() }); onSuccess() }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Fehler') }
    finally { setSaving(false) }
  }
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {[
        { label: 'Mitarbeiter', el: <select value={formData.user_id} onChange={(e) => setFormData({ ...formData, user_id: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm" required><option value="">Auswählen...</option>{users.map(u => <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.name}</option>)}</select> },
        { label: 'Typ', el: <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"><option value="Kommen">Kommen</option><option value="Gehen">Gehen</option></select> },
        { label: 'Datum & Uhrzeit', el: <Input type="datetime-local" value={formData.timestamp} onChange={(e) => setFormData({ ...formData, timestamp: e.target.value })} required /> },
        { label: 'Bauvorhaben', el: <select value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"><option value="">Kein Bauvorhaben</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select> },
        { label: 'Grund für manuelle Eingabe', el: <Input value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="z.B. Vergessene Stempelung" required /> },
      ].map(f => <div key={f.label} className="space-y-2"><label className="text-sm font-medium">{f.label}</label>{f.el}</div>)}
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
        <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern</Button>
      </div>
    </form>
  )
}

export default function Zeiterfassung() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'day' | 'week' | 'month'>('day')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    startDate: getWeekStart(new Date()).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    projectId: '',
  })
  const [addModal, setAddModal] = useState(false)

  // Build query params matching the old loadEntries logic
  const entryParams: Record<string, string> = (() => {
    const params: Record<string, string> = {}
    if (activeTab === 'day') {
      const d = new Date(filters.date)
      params.date = `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getFullYear()).slice(-2)}`
    } else { params.startDate = filters.startDate; params.endDate = filters.endDate }
    if (filters.projectId) params.projectId = filters.projectId
    return params
  })()

  const { data: entries = [], isLoading: loading } = useTimeEntries(entryParams)
  const { data: users = [] } = useUsers()
  const { data: projects = [] } = useProjects()

  const autoAssignMutation = useMutation({
    mutationFn: (dateStr: string) => timeApi.assignProjects({ date: dateStr }),
    onSuccess: (r) => { toast.success(r.data.message || 'Zugewiesen'); queryClient.invalidateQueries({ queryKey: ['time-entries'] }) },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler'),
  })

  const handleAutoAssign = () => {
    const d = new Date(filters.date)
    const dateStr = `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getFullYear()).slice(-2)}`
    autoAssignMutation.mutate(dateStr)
  }

  // Group by user
  const grouped: Record<number, GroupedUser> = {}
  entries.forEach(e => {
    if (!grouped[e.user_id]) grouped[e.user_id] = { user_name: e.user_name, user_id: e.user_id, entries: [], totalWorkMinutes: 0, workHours: null }
    grouped[e.user_id].entries.push(e)
  })
  Object.values(grouped).forEach(g => {
    g.entries.sort((a, b) => (a.time || '00:00').localeCompare(b.time || '00:00'))
    let total = 0, lastIn: string | null = null
    g.entries.forEach(e => {
      const t = e.time || formatTime(e.timestamp)
      if (e.type === 'Kommen') lastIn = t
      else if (e.type === 'Gehen' && lastIn) {
        const [ih, im] = lastIn.split(':').map(Number), [oh, om] = t.split(':').map(Number)
        const diff = (oh * 60 + om) - (ih * 60 + im)
        if (diff > 0) total += diff
        lastIn = null
      }
    })
    g.totalWorkMinutes = total; g.workHours = total > 0 ? Math.round((total / 60) * 100) / 100 : null
  })

  const tabLabels = { day: 'Tagesansicht', week: 'Wochenübersicht', month: 'Monatsübersicht' }

  return (
    <div>
      <PageHeader title="Zeiterfassung" description="Übersicht und Verwaltung aller Zeiteintraege" actions={
        <div className="flex gap-2">
          <Button variant="outline" className="bg-white" onClick={handleAutoAssign} disabled={autoAssignMutation.isPending}>{autoAssignMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Bauvorhaben zuweisen</Button>
          <Button variant="outline" className="bg-white" onClick={() => { if (activeTab === 'day') window.open(`/api/reports/daily?date=${filters.date}&format=pdf`, '_blank'); else { const d = new Date(filters.startDate || filters.date); window.open(`/api/reports/monthly?month=${String(d.getMonth()+1).padStart(2,'0')}&year=${d.getFullYear()}&format=pdf`, '_blank') } }}><Download className="h-4 w-4" /> Exportieren</Button>
          <Button variant="outline" className="bg-white" onClick={() => setAddModal(true)}><Plus className="h-4 w-4" /> Eintrag hinzufügen</Button>
        </div>
      } />

      <div className="mb-6 flex gap-2">
        {(['day', 'week', 'month'] as const).map(tab => (
          <Button key={tab} variant={activeTab === tab ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab(tab)}>{tabLabels[tab]}</Button>
        ))}
      </div>

      <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
        <div className="relative min-w-[200px] max-w-sm flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Suche..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
        {activeTab === 'day' ? (
          <Input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className="w-48" />
        ) : (<>
          <Input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} className="w-40" />
          <Input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} className="w-40" />
        </>)}
        <select value={filters.projectId} onChange={(e) => setFilters({ ...filters, projectId: e.target.value })} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
          <option value="">Alle Bauvorhaben</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </CardContent></Card>

      {loading ? <div className="space-y-2">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-16 w-full" />)}</div> : activeTab === 'day' ? (
        (() => {
          const filteredGroups = Object.values(grouped).filter(g => !searchTerm || g.user_name?.toLowerCase().includes(searchTerm.toLowerCase()));
          return filteredGroups.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">{searchTerm ? 'Kein Mitarbeiter gefunden' : 'Keine Einträge für diesen Tag'}</CardContent></Card> : (
          <div className="space-y-4">
            {filteredGroups.map(g => (
              <Card key={g.user_id} className="overflow-hidden">
                <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{g.user_name}</span>
                    <span className="text-sm text-muted-foreground">({g.entries.length} {g.entries.length === 1 ? 'Eintrag' : 'Einträge'})</span>
                  </div>
                  {g.workHours ? <span className="text-sm font-medium">Gesamt: <span className="text-primary-600">{formatHours(g.workHours)}</span></span> : <span className="text-sm text-muted-foreground">Arbeitszeit: -</span>}
                </div>
                <div className="overflow-x-auto"><table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {['Typ','Uhrzeit','Bauvorhaben','Standort'].map(h => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">{h}</th>)}
                  </tr></thead>
                  <tbody>{g.entries.map((e, i) => (
                    <tr key={e.id || i} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-2"><Badge variant={e.type === 'Kommen' ? 'success' : 'info'}>{e.type}</Badge></td>
                      <td className="px-4 py-2 text-sm font-medium">{e.time || formatTime(e.timestamp)}</td>
                      <td className="px-4 py-2 text-sm">{e.project_name ? <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-primary-500" />{e.project_name}{e.auto_assigned && <Badge variant="success" className="text-[10px]">Auto</Badge>}</span> : <span className="text-muted-foreground">Nicht zugewiesen</span>}</td>
                      <td className="px-4 py-2 text-sm">{e.location_url ? <a href={e.location_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary-500 hover:underline"><MapPin className="h-4 w-4" />Karte</a> : '-'}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              </Card>
            ))}
          </div>
        )})()
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white"><table className="w-full">
          <thead><tr className="border-b bg-muted/50">
            {['Mitarbeiter','Datum','Typ','Uhrzeit','Bauvorhaben','Standort'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
          </tr></thead>
          <tbody>
            {(() => { const fe = searchTerm ? entries.filter((e: any) => e.user_name?.toLowerCase().includes(searchTerm.toLowerCase())) : entries; return fe.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">{searchTerm ? 'Kein Mitarbeiter gefunden' : 'Keine Einträge'}</td></tr> :
            fe.map(e => (
              <tr key={e.id} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-4 py-3 text-sm font-medium">{e.user_name}</td>
                <td className="px-4 py-3 text-sm">{e.date}</td>
                <td className="px-4 py-3"><Badge variant={e.type === 'Kommen' ? 'success' : 'info'}>{e.type}</Badge></td>
                <td className="px-4 py-3 text-sm">{e.time || formatTime(e.timestamp)}</td>
                <td className="px-4 py-3 text-sm">{e.project_name ? <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-primary-500" />{e.project_name}</span> : '-'}</td>
                <td className="px-4 py-3 text-sm">{e.location_url ? <a href={e.location_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary-500 hover:underline"><MapPin className="h-4 w-4" />Karte</a> : '-'}</td>
              </tr>
            )); })()}
          </tbody>
        </table></div>
      )}

      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent><DialogHeader><DialogTitle>Zeiteintrag hinzufügen</DialogTitle></DialogHeader>
          <AddEntryForm users={users} projects={projects} onClose={() => setAddModal(false)} onSuccess={() => { setAddModal(false); queryClient.invalidateQueries({ queryKey: ['time-entries'] }); toast.success('Eintrag hinzugefügt') }} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
